import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { CloudRain, Search, AlertTriangle, CheckCircle2, CalendarDays, Loader2, ArrowRight } from 'lucide-react';
import SEO from '@/components/seo/SEO';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { format, subDays } from 'date-fns';
import { getSignupUrl } from '@/lib/appLinks';
import { trackSignupIntent } from '@/lib/tracking';
import { stateRegulations } from '@/lib/stateData';

interface RainEvent {
  date: string;
  amount: number;
}

interface ResolvedLocation {
  name: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
}

const RainfallHistoryPage: React.FC = () => {
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<RainEvent[] | null>(null);
  const [error, setError] = useState('');
  const [threshold, setThreshold] = useState(0.25);
  const [resolvedLocation, setResolvedLocation] = useState<ResolvedLocation | null>(null);
  
  const appSignUpUrl = getSignupUrl(undefined, '/sites?onboarding=weather');

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!location) return;
    
    setLoading(true);
    setError('');
    setEvents(null);

    try {
      // 1. Geocode the location string
      const geoUrl = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(location)}&count=1&language=en&format=json`;
      const geoResponse = await fetch(geoUrl);
      if (!geoResponse.ok) throw new Error('Location lookup is temporarily unavailable.');
      const geoData = await geoResponse.json();
      
      if (!geoData.results || geoData.results.length === 0) {
        throw new Error("Location not found. Try a city name (e.g., 'Austin, TX').");
      }

      const { latitude, longitude, name, admin1 } = geoData.results[0];
      const state = Object.values(stateRegulations).find((entry) => entry.name === admin1)?.abbreviation || '';
      const displayName = `${name}, ${state || admin1 || ''}`;

      // 2. Fetch Historical Weather (Last 30 Days)
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const startDate = format(subDays(new Date(), 30), 'yyyy-MM-dd');
      
      const weatherUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${latitude}&longitude=${longitude}&start_date=${startDate}&end_date=${endDate}&daily=precipitation_sum&timezone=auto&precipitation_unit=inch`;
      const weatherResponse = await fetch(weatherUrl);
      if (!weatherResponse.ok) throw new Error('Historical weather is temporarily unavailable.');
      const weatherData = await weatherResponse.json();
      
      const daily = weatherData.daily;
      const results: RainEvent[] = [];

      daily.time.forEach((date: string, i: number) => {
          const amount = daily.precipitation_sum[i];
          if (amount >= 0.1) { 
              results.push({
                  date,
                  amount
              });
          }
      });

      setEvents(results.reverse());
      setLocation(displayName);
      setResolvedLocation({ name: displayName, city: name, state, latitude, longitude });

    } catch (err: any) {
        console.error(err);
        setError(err.message || "Failed to fetch weather data.");
    } finally {
        setLoading(false);
    }
  };

  const handleSaveLocation = () => {
    if (resolvedLocation) {
      try {
        sessionStorage.setItem('runofftrack.site_draft', JSON.stringify({
          version: 1,
          source: 'rain_event_finder',
          name: `${resolvedLocation.name} Jobsite`,
          permitNumber: '',
          address: '',
          city: resolvedLocation.city,
          state: resolvedLocation.state,
          lat: resolvedLocation.latitude.toFixed(6),
          lng: resolvedLocation.longitude.toFixed(6),
          frequencyDays: '7'
        }));
      } catch {
        // Continue to signup even when browser storage is unavailable.
      }
    }
    trackSignupIntent('rain_finder_result');
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "SWPPP Rain Event Finder",
    "description": "Check historical precipitation for a US location using a threshold selected from your project permit.",
    "applicationCategory": "UtilitiesApplication",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
  };

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-blue-500/20">
      <SEO 
        title="SWPPP Historical Rainfall Checker"
        description="Review 30 days of historical precipitation for a jobsite using the rainfall threshold in your project permit."
        canonical="/tools/rain-event-finder"
        jsonLd={jsonLd}
      />

      <div className="bg-muted/30 pt-32 pb-20 border-b border-border">
        <div className="container px-4 text-center max-w-4xl mx-auto">
           <Breadcrumbs items={[
               { label: 'Resources', href: '/' },
               { label: 'Rain Event Finder', href: '/tools/rain-event-finder' }
           ]} />

           <div className="inline-flex items-center justify-center p-3 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full mb-6">
             <CloudRain size={32} />
           </div>
           <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
             Historical Rainfall Checker
           </h1>
           <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
             Enter your jobsite location to review precipitation from the last 30 days, then compare it with your permit-defined inspection schedule.
           </p>

           <Card className="max-w-md mx-auto shadow-xl border-border">
               <CardContent className="p-6">
                   <form onSubmit={handleCheck} className="space-y-3">
                       <label className="block text-left text-xs font-semibold text-muted-foreground" htmlFor="rain-threshold">Rainfall threshold from your permit</label>
                       <select
                         id="rain-threshold"
                         value={threshold}
                         onChange={(event) => setThreshold(Number(event.target.value))}
                         className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                       >
                         <option value={0.25}>0.25 inches</option>
                         <option value={0.5}>0.50 inches</option>
                         <option value={0.75}>0.75 inches</option>
                       </select>
                       <div className="flex flex-col gap-2 sm:flex-row">
                       <div className="relative flex-1">
                           <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                           <Input 
                                placeholder="City, Zip, or Address..." 
                                className="pl-9"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                disabled={loading}
                           />
                       </div>
                       <Button type="submit" className="w-full sm:w-auto" disabled={loading || !location}>
                           {loading ? <Loader2 className="animate-spin h-4 w-4"/> : "Check History"}
                       </Button>
                       </div>
                   </form>
                   {error && <p className="text-red-500 text-xs mt-2 text-left">{error}</p>}
               </CardContent>
           </Card>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-3xl mx-auto">
        {events && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 mb-24">
                <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold">Rainfall History (Last 30 Days)</h3>
                    <span className="text-sm text-muted-foreground">Source: Open-Meteo archive</span>
                </div>

                {events.length === 0 ? (
                    <div className="p-12 text-center bg-muted/20 rounded-xl border border-dashed">
                        <CheckCircle2 className="mx-auto h-12 w-12 text-green-500 mb-4" />
                        <h3 className="text-lg font-medium">No Significant Rain</h3>
                        <p className="text-muted-foreground">We didn't detect any events &gt; 0.10" in the last 30 days.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {events.map((event, idx) => {
                            const meetsThreshold = event.amount >= threshold;
                            return (
                            <div 
                                key={idx} 
                                className={`flex items-center justify-between p-4 rounded-xl border transition-all ${meetsThreshold ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-900/30' : 'bg-card border-border'}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`p-2 rounded-lg ${meetsThreshold ? 'bg-orange-100 text-orange-700' : 'bg-muted text-muted-foreground'}`}>
                                        <CalendarDays size={20} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-foreground">
                                            {new Date(event.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                            {event.amount.toFixed(2)}" Precipitation
                                        </div>
                                    </div>
                                </div>
                                
                                {meetsThreshold ? (
                                    <div className="flex items-center gap-2 text-orange-700 font-bold text-sm bg-orange-100 px-3 py-1 rounded-full dark:bg-orange-900/40 dark:text-orange-300">
                                        <AlertTriangle size={14} /> MEETS THRESHOLD
                                    </div>
                                ) : (
                                    <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">Below threshold</span>
                                )}
                            </div>
                        )})}
                    </div>
                )}

                <div className="bg-primary text-primary-foreground p-8 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
                    <div>
                        <h4 className="text-2xl font-bold mb-2">Keep the location with your project.</h4>
                        <p className="text-primary-foreground/80 max-w-md">
                            Carry these coordinates into RunOffTrack, capture weather with inspections, and keep signed reports organized by site.
                        </p>
                    </div>
                    <Button variant="secondary" size="lg" className="whitespace-nowrap" asChild>
                        <a href={appSignUpUrl} onClick={handleSaveLocation}>Save This Jobsite <ArrowRight className="ml-2 h-4 w-4"/></a>
                    </Button>
                </div>
            </div>
        )}

        <div className="prose dark:prose-invert max-w-none pt-8 border-t border-border">
            <h2>Use the threshold in your permit</h2>
            <p>
                Inspection schedules and rain thresholds vary by permit and permitting authority. Under the 2022 EPA CGP, one available schedule combines inspections every 14 calendar days with inspections after a storm producing 0.25 inches or more of rain within 24 hours.
            </p>
            <ol>
                <li>Confirm the schedule elected for your project.</li>
                <li>Confirm how the permit defines a qualifying storm and its inspection deadline.</li>
                <li>Use the selector above only after checking those project-specific requirements.</li>
            </ol>
            <p>
                Some state permits use different thresholds or schedules. Historical gridded weather data is a useful cross-check, but it may not replace an on-site gauge or another measurement method required by your permit.
            </p>

            <h3>Keep a traceable weather record</h3>
            <p>
                Record the gauge or weather-station source required by your permit, the observation period, and the measured amount. If equipment is damaged or data is missing, document the issue rather than reconstructing a record without support.
            </p>

            <h3>Open-Meteo data vs. on-site gauges</h3>
            <p>
                This tool uses the Open-Meteo historical archive. Treat it as a screening and cross-checking aid. Follow the measurement source and recordkeeping method specified by the applicable permit.
            </p>

            <h3>What if I missed an inspection?</h3>
            <p>
                If you discover a past rain event that you missed:
            </p>
            <ul>
                <li><strong>Do NOT backdate your report.</strong> This is fraud.</li>
                <li>Conduct an inspection immediately.</li>
                <li>Note the gap in your SWPPP Corrective Action Log.</li>
                <li>Document the process change you will use to prevent recurrence.</li>
            </ul>
        </div>
      </div>
    </div>
  );
};

export default RainfallHistoryPage;
